// parse post from any format to Post type
import { DATE_FORMAT } from "../../constants/common";
import {
  NotificationPerson,
  PlatformTypes,
  Post,
  PostStates,
  PostType,
  TiktokPost,
  ThreadsPost,
  YoutubePost,
  IgCollabPerson,
} from "./types";
import {
  VIDEO_COVER_TIMESTAMPS,
  VIDEO_COVER_TYPES,
  VideoCoverType,
} from "../../constants/VideoCoverConstants";
import { isValid } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { safeParseDateString } from "../../utils/date-helpers";
import { useSelector } from "react-redux";
import { PostMediaFile } from "../../shared/types/post";
import { removeZeroWidthSpace } from "../../utils/text";
import { FIELDS_DEFAULT } from "../../constants/PostFormFields";
import {
  type GmbPostFields,
  mergeGmbPostFields,
} from "./utils/gmb-post-fields";
import { AccountPlatformType, AccountType } from "../../shared/types/accounts";
import { PlatformSpec } from "../../constants/Platforms";
import { hasVideoExtension } from "../../utils/media";
import { SaveFormResult } from "../../components/Posts/PostForm/types";
import { pickStoryFields } from "./pick-story-fields";
import type {
  FormPlatformType,
  InstagramPostOptions,
} from "../../shared/types/post";

type AccountMap = Record<string, Account>;
type PostWithoutState = Omit<Post, "state" | "when">;
type ParseablePost = NgPost & Partial<Post> & { id?: string };

export const groupAccountIdsByPlatformType = (
  allAccounts: AccountMap,
  accountsNeedGrouped: string[]
) => {
  return accountsNeedGrouped.reduce<Record<string, string[]>>(
    (acum, accountId) => {
      const account = allAccounts[accountId];

      if (!account) return acum;

      const platformType = account.platformType;

      if (!platformType) return acum;

      acum[platformType] = [...(acum[platformType] || []), accountId];

      return acum;
    },
    {}
  );
};

export const SHARED_MEDIA_FIELD_KEYS = [
  "mediaFiles",
  "url",
  "origUrl",
  "originalUrl",
  "thumbnailUrl",
  "fileName",
  "meta",
  "altText",
  "isVideo",
  "isDocument",
] as const;

export function hasRootMedia(post: Post): boolean {
  return Boolean(post.url || post.origUrl || post.originalUrl);
}

function hasExplicitSelectedVideoCover(post: Post): boolean {
  if (!post.thumbnailUrl) {
    return false;
  }

  return (
    post.videoCoverType === VIDEO_COVER_TYPES.UPLOADED ||
    post.videoCoverType === VIDEO_COVER_TYPES.FRAME_SELECTED ||
    post.videoCoverImageTimestamp === VIDEO_COVER_TIMESTAMPS.UPLOADED_COVER ||
    (typeof post.videoCoverImageTimestamp === "number" &&
      post.videoCoverImageTimestamp > VIDEO_COVER_TIMESTAMPS.DEFAULT_FRAME)
  );
}

export function normalizePostMediaShape(post: Post): Post {
  const mediaFiles = Array.isArray(post.mediaFiles) ? post.mediaFiles : [];

  const normalizedMediaFiles =
    mediaFiles.length === 0 && hasRootMedia(post) && post.type !== "text"
      ? [parsePostAsMediaFile(post)]
      : mediaFiles;

  if (normalizedMediaFiles.length === 0) {
    return {
      ...post,
      mediaFiles: normalizedMediaFiles,
    };
  }

  const [primaryMedia] = normalizedMediaFiles;
  const primaryOriginalUrl =
    primaryMedia.originalUrl ||
    primaryMedia.origUrl ||
    post.originalUrl ||
    post.origUrl;
  const thumbnailUrl = hasExplicitSelectedVideoCover(post)
    ? post.thumbnailUrl
    : primaryMedia.thumbnailUrl || post.thumbnailUrl;

  return {
    ...post,
    mediaFiles: normalizedMediaFiles,
    url: primaryMedia.url || post.url,
    originalUrl: primaryOriginalUrl || undefined,
    origUrl: primaryOriginalUrl || undefined,
    thumbnailUrl,
    fileName: primaryMedia.fileName || post.fileName,
    meta: primaryMedia.meta || post.meta,
    altText: primaryMedia.altText ?? post.altText,
  } as Post;
}

export function mergePlatformPostFields(
  post: Post,
  platformFields?: Partial<Post> & Partial<GmbPostFields>
): Post {
  if (!platformFields) {
    return normalizePostMediaShape(post);
  }

  const gmbPlatformFields = platformFields as Partial<GmbPostFields>;
  const hasGmbFields =
    gmbPlatformFields.gmbEvent ||
    gmbPlatformFields.gmbOffer ||
    gmbPlatformFields.gmbCallToAction ||
    gmbPlatformFields.gmbTopicType;

  let mergedPost: Post;

  if (hasGmbFields) {
    mergedPost = mergeGmbPostFields(
      post as unknown as Record<string, unknown>,
      platformFields as unknown as Record<string, unknown>
    ) as Post;
  } else {
    mergedPost = {
      ...post,
      ...platformFields,
    } as Post;
  }

  for (const field of SHARED_MEDIA_FIELD_KEYS) {
    if (platformFields[field] === undefined && post[field] !== undefined) {
      mergedPost[field] = post[field] as never;
    }
  }

  return normalizePostMediaShape(mergedPost);
}

export function parseFormDataToPost(
  {
    post,
    newPostState,
    platformFields,
  }: Pick<SaveFormResult, "post" | "newPostState" | "platformFields">,
  accounts: { [key: string]: Account },
  userTimezone: string
): Post | Post[] {
  const excludedPlatformsTypes = Object.keys(
    platformFields
  ) as FormPlatformType[];

  // if posts has one account or (many accounts and no any unlinked fields) just return one post
  if (excludedPlatformsTypes.length === 0) {
    return parseFormPostToTypedPost(post, accounts, newPostState, userTimezone);
  } else {
    const postAccountId = post.accountId;
    const normalizedAccounts = Object.keys(accounts).reduce((acum, id) => {
      acum[id] = normalizeAccount(accounts[id]);

      return acum;
    }, {} as AccountMap);

    const groupedAccountIdsByPlatformType = groupAccountIdsByPlatformType(
      normalizedAccounts,
      post.accountIds.filter((accountId) => accountId !== postAccountId)
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { _id, id, ...initialPost } = post;

    const newPosts: Post[] = [];

    // post can no has one accountId (e.g. draft post), so we need to separate (one part is edit post and others is new posts)
    const postAccount: Partial<Account> =
      (postAccountId && normalizedAccounts[postAccountId]) || {};
    const postPlatformType =
      postAccountId && (post.platformType || postAccount.platformType)
        ? post.platformType || postAccount.platformType
        : excludedPlatformsTypes[0] || "LINKED";
    const postAccountIds = postAccountId
      ? [postAccountId]
      : groupedAccountIdsByPlatformType[postPlatformType] || [];

    const editPost: Post | undefined =
      post._id || post.id
        ? parseFormPostToTypedPost(
            mergePlatformPostFields(
              {
                ...post,
                accountIds: postAccountIds,
              } as Post,
              platformFields[postPlatformType]
            ),
            accounts,
            newPostState,
            userTimezone
          )
        : undefined;

    let addedAccounts: string[] = [...postAccountIds];
    for (const accountId of post.accountIds) {
      if (addedAccounts.includes(accountId)) continue;

      const account = normalizedAccounts[accountId];
      // post can has many accounts by one type (e.g. facebook account1, facebook account2, ...),
      // this is combine to one post (we not separate posts by type and account)
      const newPostAccountIds =
        groupedAccountIdsByPlatformType[account.platformType || "IG"] || [];
      // not generate new post with the same accounts
      addedAccounts = [...addedAccounts, ...newPostAccountIds];

      const accountPlatformType = account.platformType || "IG";

      const newPost: Post = parseFormPostToTypedPost(
        mergePlatformPostFields(
          {
            ...(initialPost as Post),
            accountIds: newPostAccountIds,
          },
          platformFields[accountPlatformType]
        ),
        accounts,
        newPostState,
        userTimezone
      );

      newPosts.push(newPost);
    }

    return [...(editPost ? [editPost] : []), ...newPosts];
  }
}

function normalizeAccount(account: AccountType): Account {
  // some accounts don't have platformType
  return {
    ...account,
    platformType:
      PlatformNameToPlatformTypeMap[account.platformName ?? ""] ||
      account.platformType,
  };
}

export const PlatformNameToPlatformTypeMap: Record<
  string,
  AccountPlatformType
> = {
  instagram: "IG",
  facebook: "FB",
  twitter: "TW",
  linkedin: "LI",
  googlemybusiness: "GMB",
  youtube: "YT",
  pinterest: "PI",
  tiktok: "TT",
  threads: "TH",
  snapchat: "SC",
  other: "OT",
};

export function parseFormPostToTypedPost(
  post: Post,
  accounts: AccountMap,
  newPostState: string,
  userTimezone: string
): Post & { isNow?: boolean } {
  post = normalizePostMediaShape(post);
  const state = getPostTypeFromFormState(newPostState);
  // ensure isNow is set to false for drafts or queued posts
  // as they can't be published 'now'
  let isNow = post.isNow;
  if (
    (state === PostStates.draft || state === PostStates.queue) &&
    isNow === true
  ) {
    isNow = false;
  }
  const postWithoutState = parsePostToTypesPostWithoutState(
    post as ParseablePost,
    accounts
  );

  const postWithState = {
    ...postWithoutState,
    state,
    isNow: isNow,
    videoPostType: post.videoPostType,
    when: (() => {
      const needsWhen =
        [PostStates.schedule, PostStates.queue, PostStates.archived].includes(
          state
        ) && !isNow;
      if (!needsWhen || !post.when) return undefined;
      const whenDate = safeParseDateString(post.when, userTimezone);
      if (!isValid(whenDate)) {
        console.error(`[format-post] Invalid when date: "${post.when}"`);
        return undefined;
      }
      return formatInTimeZone(whenDate, userTimezone, DATE_FORMAT);
    })(),
  } as Post & { isNow?: boolean };

  return postWithState;
}

// we have to use it till we don't know about post state
export function parsePostToTypesPostWithoutState(
  post: ParseablePost,
  accounts: AccountMap
): PostWithoutState {
  const postId = post._id || post.id;
  const accountIds = (post.accountIds || [post.accountId]).filter(
    (accountId): accountId is string => Boolean(accountId)
  );

  const platformType = getPostPlatform({ accountIds }, accounts);

  const postPlatforms = accountIds
    .filter((accountId: string) => accounts[accountId])
    .map((accountId: string) => {
      const platformType = accounts[accountId].platformType;

      return platformType ? platformType : "IG";
    });

  const commonPostInfo: Partial<Post> = {
    _id: postId,
    isSuccess: post.isSuccess,
    accountIds,
    labelIds: post.labelIds || [],
    postClusterId: post.postClusterId,
    ideaId: post.ideaId,
    channel: post.channel,
    caption: removeZeroWidthSpace(post.caption),
    postStatusKey: post.postStatusKey,
    thumbnailUrl: post.thumbnailUrl,
    isVideo: post.isVideo || post.type === "fb-reel",
    ...parsePostMediaFiles(post),
    redirectUrl: post.redirectUrl,
    platforms: postPlatforms,
    videoPostType: post.videoPostType,
  };

  const hasIG = postPlatforms.includes("IG");
  const hasGMB = postPlatforms.includes("GMB");
  const hasPI = postPlatforms.includes("PI");
  const hasYT = postPlatforms.includes("YT");
  const hasTT = postPlatforms.includes("TT");
  const hasTH = postPlatforms.includes("TH");
  const hasFB = postPlatforms.includes("FB");
  const hasLI = postPlatforms.includes("LI");

  switch (platformType) {
    case PlatformTypes.instagram: {
      return {
        ...commonPostInfo,
        firstcomment: removeZeroWidthSpace(post.firstcomment),
        platformType: PlatformTypes.instagram,
        location: post.location,
        tags: post.tags || [],
        productTags: post.productTags || [],
        isStory: post.isStory,
        /*
         * Read on EVERY post type, not only stories. SKE-3324.
         *
         * This sat inside the story spread below, so an image or video post
         * came back from the API with the flag set and the composer dropped it
         * on the floor. The server now persists it for every type, and a load
         * path that discards what the save path stored is the same
         * silently-wrong state the ticket describes, just moved one layer out.
         *
         * The four STORY fields stay in the spread: they are meaningless on a
         * non-story post and `storyStickerHeight` carries a default that would
         * otherwise be invented for posts that have no sticker.
         */
        publishStoryManually: post.publishStoryManually,
        ...(post.isStory ? pickStoryFields(post) : {}),
        ...parseInstagramData(post as NgPost),
        ...parseCoverImageFields(post as NgPost),
      } as PostWithoutState;
    }
    case PlatformTypes.facebook: {
      return {
        ...commonPostInfo,
        firstcomment: removeZeroWidthSpace(post.firstcomment),
        platformType: PlatformTypes.facebook,
        location: post.location,
        ...parseCoverImageFields(post as NgPost),
        isFbReel: (post as Partial<Post>).isFbReel,
        ...parseStoryData(post),
      } as PostWithoutState;
    }
    case PlatformTypes.twitter: {
      return {
        platformType: PlatformTypes.twitter,
        ...commonPostInfo,
      } as PostWithoutState;
    }
    case PlatformTypes.linkedin: {
      return {
        platformType: PlatformTypes.linkedin,
        firstcomment: removeZeroWidthSpace(post.firstcomment),
        ...commonPostInfo,
        ...parseLinkedInData(post as NgPost),
        ...parseCoverImageFields(post as NgPost),
      } as PostWithoutState;
    }
    case PlatformTypes.pinterest: {
      return {
        platformType: PlatformTypes.pinterest,
        ...commonPostInfo,
        ...parsePinterestData(post as NgPost),
      } as PostWithoutState;
    }
    case PlatformTypes.googlemybusiness: {
      // TODO
      return {
        platformType: PlatformTypes.googlemybusiness,
        ...commonPostInfo,
        ...parseGoogleBusinessData(post as NgPost),
      } as PostWithoutState;
    }
    case PlatformTypes.youtube: {
      return {
        platformType: PlatformTypes.youtube,
        ...commonPostInfo,
        ...parseYoutubeData(post as YoutubePost),
      } as PostWithoutState;
    }
    case PlatformTypes.snapchat: {
      return {
        platformType: PlatformTypes.snapchat,
        ...commonPostInfo,
      } as PostWithoutState;
    }
    case PlatformTypes.tiktok: {
      return {
        platformType: PlatformTypes.tiktok,
        ...commonPostInfo,
        ...parseTikTokData(post as TiktokPost),
      } as PostWithoutState;
    }
    case PlatformTypes.threads: {
      return {
        platformType: PlatformTypes.threads,
        ...commonPostInfo,
        ...parseThreadsData(post as ThreadsPost),
      } as PostWithoutState;
    }
    case PlatformTypes.other: {
      return {
        platformType: PlatformTypes.other,
        ...commonPostInfo,
      } as PostWithoutState;
    }
    case PlatformTypes.cluster: {
      return {
        ...commonPostInfo,
        firstcomment: post.firstcomment,
        platformType: PlatformTypes.cluster,
        location: post.location,
        tags: post.tags,
        productTags: post.productTags,
        ...(hasGMB ? parseGoogleBusinessData(post as NgPost) : {}),
        ...(hasPI ? parsePinterestData(post as NgPost) : {}),
        ...(hasYT ? parseYoutubeData(post as YoutubePost) : {}),
        ...(hasTT ? parseTikTokData(post as TiktokPost) : {}),
        ...(hasIG ? parseInstagramData(post as NgPost) : {}),
        ...(hasTH ? parseThreadsData(post as ThreadsPost) : {}),
        ...(hasIG || hasFB || hasLI
          ? parseCoverImageFields(post as NgPost)
          : {}),
        ...(hasFB ? { isFbReel: (post as Partial<Post>).isFbReel } : {}),
        // An Instagram and Facebook story is a cluster (IDEA-622).
        ...(hasIG || hasFB ? parseStoryData(post) : {}),
      } as PostWithoutState;
    }
    default: {
      return {
        ...commonPostInfo,
        platformType: PlatformTypes.cluster,
      } as PostWithoutState;
    }
  }
}

export const parseGoogleBusinessData = function (post: NgPost) {
  const topicType = post.gmbTopicType || "STANDARD";
  return {
    gmbTopicType: topicType,
    ...(topicType === "OFFER"
      ? {
          gmbEvent: {
            title: post.gmbEvent?.title,
            // Will send to backend as in user timezone - on creation we will need to parse to UTC
            startDate: post.gmbEvent?.startDate,
            endDate: post.gmbEvent?.endDate,
          },
          gmbOffer: post.gmbOffer,
        }
      : {}),
    ...(topicType === "STANDARD"
      ? {
          gmbCallToAction: post.gmbCallToAction,
        }
      : {}),
    ...(topicType === "EVENT"
      ? {
          gmbEvent: post.gmbEvent,
          gmbCallToAction: post.gmbCallToAction,
        }
      : {}),
    ...(topicType === "ALERT"
      ? {
          gmbCallToAction: post.gmbCallToAction,
        }
      : {}),
  };
};

/**
 * The story fields a Facebook or cluster post carries, read the way the
 * Instagram case reads them, so a Facebook story edit round-trips (IDEA-622).
 * Only a story gets them: a feed post keeps its shape.
 */
function parseStoryData(post: ParseablePost): Partial<Post> {
  const source = post as Partial<Post>;
  if (!source.isStory && source.type !== "story") {
    return {};
  }

  return { isStory: true, ...pickStoryFields(source) };
}

export function parsePinterestData(post: NgPost) {
  return {
    piBoards: post.piBoards || [],
    piSectionId: post.piSectionId || null,
    piTitle: post.piTitle || null,
  };
}

export function parseLinkedInData(post: NgPost) {
  return {
    liTargetEntities: post.liTargetEntities || [],
    // LI document posts
    fileName: post.fileName || undefined,
    meta: post.meta || {},
  };
}

export function parseYoutubeData(post: YoutubePost) {
  return {
    ytTitle: post.ytTitle,
    ytCategoryId: post.ytCategoryId,
    ytTags: post.ytTags || null,
    ytPrivacy: post.ytPrivacy,
  };
}

export function parseCoverImageFields(post: NgPost) {
  const {
    videoCoverPreview,
    videoCoverImageTimestamp,
    thumbnailUrl,
    videoCoverType,
  } = post;

  // Migration logic: convert legacy -1 timestamps to new cover type system
  let migratedCoverType = videoCoverType;
  if (!videoCoverType && videoCoverImageTimestamp === -1) {
    migratedCoverType = "uploaded";
  } else if (!videoCoverType && videoCoverImageTimestamp === 0) {
    migratedCoverType = "platform_default";
  } else if (
    !videoCoverType &&
    videoCoverImageTimestamp !== undefined &&
    videoCoverImageTimestamp > 0
  ) {
    migratedCoverType = "frame_selected";
  }

  return {
    thumbnailUrl,
    videoCoverPreview,
    videoCoverImageTimestamp,
    videoCoverType: migratedCoverType,
  };
}
/**
 * SKE-4605. The server reads an omitted `instagram` key as "unchanged", and
 * clears the stored options only when it is HANDED a value. So form state for a
 * post with Instagram accounts always carries the object, empty when the post
 * has no options, which is what lets a customer switch every option back off.
 *
 * Both call sites have already established that the post has Instagram
 * accounts, which is why the empty object is unconditional here.
 */
export function parseInstagramData(post: NgPost) {
  const { collaboratingIgUsers, instagram } = post as NgPost & {
    instagram?: InstagramPostOptions | null;
  };

  return {
    collaboratingIgUsers,
    instagram: instagram ?? {},
  };
}

export function parseTikTokData(post: TiktokPost): Partial<TiktokPost> {
  const {
    // default values
    videoCoverImageTimestamp,
    videoCoverPreview,
    thumbnailUrl,
    postOptions = {
      isNotification: FIELDS_DEFAULT.IS_NOTIFICATION_POST_ONLY_DEFAULT,
      allowComments: FIELDS_DEFAULT.TT_ALLOW_COMMENTS,
      allowStitch: FIELDS_DEFAULT.TT_ALLOW_STITCH,
      allowDuet: FIELDS_DEFAULT.TT_ALLOW_DUET,
      title: "",
      autoAddMusic: FIELDS_DEFAULT.TT_AUTO_ADD_MUSIC,
      privacyLevel: FIELDS_DEFAULT.TT_PRIVACY_LEVEL,
      photoCoverIndex: 0,
    },
  } = post;

  return {
    thumbnailUrl,
    videoCoverImageTimestamp,
    videoCoverPreview,
    postOptions: {
      isNotification: postOptions.isNotification,
      allowComments: postOptions.allowComments,
      allowStitch: postOptions.allowStitch,
      allowDuet: postOptions.allowDuet,
      title: postOptions.title,
      autoAddMusic: postOptions.autoAddMusic,
      privacyLevel:
        postOptions.privacyLevel as TiktokPost["postOptions"]["privacyLevel"],
      photoCoverIndex: postOptions.photoCoverIndex,
    },
  };
}
export function parseThreadsData(post: ThreadsPost) {
  const {
    postOptions = {
      isNotification: FIELDS_DEFAULT.IS_NOTIFICATION_POST_ONLY_DEFAULT,
      replyControl: "everyone",
    },
  } = post;

  return {
    postOptions: {
      isNotification:
        postOptions.isNotification ??
        FIELDS_DEFAULT.IS_NOTIFICATION_POST_ONLY_DEFAULT,
      replyControl: postOptions.replyControl ?? FIELDS_DEFAULT.TH_REPLY_CONTROL,
    },
  };
}

export interface NgPost {
  altText?: string;
  accountName: string;
  clientId: string;
  url: string;
  isVideo: boolean;
  origUrl: string;
  thumbnailUrl: string;
  fileName: string;
  caption: string;
  firstcomment: string;
  redirectUrl: any;
  type: string;
  storyType?: string;
  storyUrl?: any;
  storyLabel?: string;
  storyStickerHeight?: number;
  publishStoryManually: boolean;
  when: string;
  tags: Tag[];
  productTags: any[];
  labelsId: any[];
  postStatusKey: string;
  mediaFiles: NgMediaFile[];
  media: any[];
  platforms: any[];
  originalUrl: string;
  postedBy: string;
  channel: string;
  hash: any;
  location: any;
  platformType: any;
  isStory: boolean;
  _id: string;
  accountIds: string[];
  accountId: string;
  unreadCommentsCount: number;
  meta?: object;
  ytTitle?: string;
  ytPrivacy?: string;
  ytCategoryId?: number | string;
  ytTags?: string | null;
  gmbTopicType?: string;
  gmbCallToAction?: object;
  gmbEvent?: {
    title?: string;
    startDate?: Date;
    endDate?: Date;
  };
  gmbOffer?: object;
  piBoards?: any[];
  piSectionId?: string;
  piTitle?: string;
  pushNotificationMeta?: NotificationPerson[];
  isPosting?: boolean;
  isQueued?: boolean;
  queued?: boolean;
  isSuccess?: boolean;
  isCanceled?: boolean;
  isPotentialError?: boolean;
  liTargetEntities?: {
    [key: string]: string[];
  };
  videoCoverImageTimestamp?: number;
  videoCoverPreview?: string;
  videoCoverType?: VideoCoverType;
  collaboratingIgUsers?: IgCollabPerson[];
}

export interface Tag {
  value: string;
  top: string;
  left: string;
  coordinates: Coordinates;
}

export interface Coordinates {
  x: number;
  y: number;
}

export interface NgMediaFile {
  altText?: string;
  tags?: unknown[];
  productTags?: unknown[];
  fileName: string;
  isVideo: boolean;
  origUrl: string;
  originalUrl?: string;
  asset_id?: string;
  thumbnailUrl: string;
  type: string;
  url: string;
  meta?: object;
  lowQualityVideo?: PostMediaFile["lowQualityVideo"];
  transcodedVideo?: PostMediaFile["transcodedVideo"];
  coverFrames?: PostMediaFile["coverFrames"];
}

export function parseNgDataToPost(
  ngPost: NgPost,
  accounts: AccountMap,
  previousPostState: string,
  userTimezone: string
): Post {
  return parseFormPostToTypedPost(
    ngPost as unknown as Post,
    accounts,
    previousPostState,
    userTimezone
  );
}

export function getPostTypeFromFormState(state: string): PostStates {
  return (
    {
      queued: PostStates.queue,
      scheduled: PostStates.schedule,
      // TODO: replace with correct backend fix
      // 'upcoming' comes from the backend
      upcoming: PostStates.schedule,
      draft: PostStates.draft,
      archived: PostStates.archived,
    }[state.toLowerCase()] ?? PostStates.draft
  );
}

export function getPostState(post: Post) {
  if (!post.when) {
    return PostStates.draft;
  }
  return post.queued ? PostStates.queue : PostStates.schedule;
}

function parsePostMediaFiles(post: ParseablePost): {
  mediaFiles: PostMediaFile[];
  type: PostType;
} {
  const mediaFiles = ((post.mediaFiles || []) as NgMediaFile[]).map(
    parseMediaFile
  );

  if (mediaFiles.length === 0 && post.url) {
    mediaFiles.push(parsePostAsMediaFile(post as Post));
  }

  const type = (() => {
    if (post.isStory || post.type === "story") {
      return "story";
    }
    if (post.mediaFiles && post.mediaFiles.length > 1) {
      return "multiMedia";
    } else if (post.type === "multiMedia") {
      // this post should not be multiMedia anymore as no mediaFiles included…
      // should not have any text posts here as no way to convert from multiMedia to text, so assume either video or image
      if (
        hasVideoExtension(post.url) ||
        mediaFiles[0].type?.includes("video")
      ) {
        // Check if this should be a reel
        if (post.videoPostType === "reel") {
          return "reel";
        }
        return "video";
      } else if (
        post.url?.includes(".pdf") ||
        mediaFiles[0].type?.includes("pdf")
      ) {
        return "document";
      } else {
        return post.url || mediaFiles[0].type?.includes("image")
          ? "image"
          : "text";
      }
    } else {
      if (!post.url) {
        return "text";
      }
      if (post.url) {
        if (
          post.url.includes(".jpg") ||
          post.url.includes(".jpeg") ||
          post.url.includes(".png") ||
          mediaFiles[0].type?.includes("image")
        ) {
          return "image";
        } else if (
          hasVideoExtension(post.url) ||
          post.url.includes(".gif") ||
          mediaFiles[0].type?.includes("video")
        ) {
          // Check if this should be a reel
          if (post.videoPostType === "reel") {
            return "reel";
          }
          return "video";
        } else if (
          post.url.includes(".pdf") ||
          mediaFiles[0].type?.includes("pdf")
        ) {
          return "document";
        } else {
          return "multiMedia";
        }
      } else {
        return "multiMedia";
      }
    }
  })();

  return {
    mediaFiles,
    type,
  };
}

export function parsePostAsMediaFile(post: Post): PostMediaFile {
  // The post's own tag arrays belong to the post, not to the single media file
  // derived from it. Letting them through would put the publish path into
  // per-slide mode on a post nobody tagged per slide. SKE-4617
  const { type, tags, productTags, ...data } = post;
  return parseMediaFile(data as unknown as NgMediaFile);
}

function getPlatformTypeFromName(platformName?: string): PlatformTypes {
  return (
    PlatformNameToTypeMap[platformName as keyof typeof PlatformNameToTypeMap] ||
    PlatformTypes.instagram
  );
}

export function parseMediaFile(mediaFile: NgMediaFile): PostMediaFile {
  const originalUrl = mediaFile.originalUrl || mediaFile.origUrl;
  const hasFileVideoExt = hasVideoExtension(mediaFile.url);
  const hasPdfFileExt = mediaFile.url?.endsWith(".pdf") ?? false;

  // delete icc if it is present in the meta object
  // otherwise we can get 413 error when creating posts
  // because the meta object is too large - and icc is not
  // used by our backend. icc is usually a giant blob of binary
  const metaWithOptionalIcc = mediaFile.meta as
    (PostMediaFile["meta"] & { icc?: unknown }) | undefined;
  if (metaWithOptionalIcc?.icc) {
    delete metaWithOptionalIcc.icc;
  }
  return {
    originalUrl,
    altText: mediaFile.altText || "",
    url: mediaFile.url,
    asset_id: mediaFile.asset_id,
    thumbnailUrl: mediaFile.thumbnailUrl,
    type:
      mediaFile.type ??
      (hasFileVideoExt ? "video" : hasPdfFileExt ? "document" : "image"),
    isVideo: mediaFile.isVideo || hasFileVideoExt,
    fileName: mediaFile.fileName,
    meta: mediaFile.meta as PostMediaFile["meta"],
    // Preserve enrichment fields the VideoCoverSelector needs so the
    // cover preview can render from coverFrames / lowQualityVideo on
    // reopen instead of downloading the full original video. SKE-3646
    lowQualityVideo: mediaFile.lowQualityVideo,
    transcodedVideo: mediaFile.transcodedVideo,
    coverFrames: mediaFile.coverFrames,
    // An allow-list, so anything not named here is dropped on reopen even
    // though the outbound mapper spreads it back out. SKE-4617
    tags: mediaFile.tags,
    productTags: mediaFile.productTags,
  };
}

export function getPostPlatform(
  post: { accountIds: string[] },
  accounts: AccountMap
): PlatformTypes {
  if (post.accountIds.length === 1) {
    return getPlatformTypeFromName(accounts[post.accountIds[0]]?.platformName);
  }

  const validAccountIds = post.accountIds.filter(
    (accountId) => accounts[accountId]
  );

  const platformsByName = Object.fromEntries(
    validAccountIds.map((accountId) => [accounts[accountId].platformName, true])
  );

  const platformsCount = Object.keys(platformsByName).length;

  if (platformsCount === 1 && accounts[validAccountIds[0]]) {
    return getPlatformTypeFromName(accounts[validAccountIds[0]].platformName);
  }

  return PlatformTypes.cluster;
}

const PlatformNameToTypeMap = {
  facebook: PlatformTypes.facebook,
  instagram: PlatformTypes.instagram,
  twitter: PlatformTypes.twitter,
  linkedin: PlatformTypes.linkedin,
  googlemybusiness: PlatformTypes.googlemybusiness,
  youtube: PlatformTypes.youtube,
  pinterest: PlatformTypes.pinterest,
  snapchat: PlatformTypes.snapchat,
  tiktok: PlatformTypes.tiktok,
  threads: PlatformTypes.threads,
  other: PlatformTypes.other,
};

// Hugh 28 Aug 2021: I think that this function is not used??
export function useTypedPost(post: NgPost | Post) {
  const accounts = useSelector(
    (state: { entities: { accounts: { byId: AccountMap } } }) =>
      state.entities.accounts.byId
  );
  return parsePostToTypesPostWithoutState(post as ParseablePost, accounts);
}

// parsed types generated by https://transform.tools/json-to-typescript
type Account = AccountType;
export type Platform = PlatformSpec;
